import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';

import { ProviderFieldsForm } from '../../../PaymentProviders/components';
import { useGetPaymentProvider } from '../../../PaymentProviders/services';
import {
  TPaymentTerminalProvider,
  TRestaurantPaymentConfig,
} from '../../../PaymentProviders/schemas/payment-provider-schema';
import {
  MASK,
  getClientFieldSchema,
  getEnvKeyShapeWarnings,
  resolveEnv,
} from '../../../PaymentProviders/utils';
import { buildClientFieldsSchema, TClientFieldsForm } from '../schemas';

type RestaurantConfigDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  providerId: string | null;
  /** The already-loaded row being edited. The parent page holds the configs, so
   * the dialog does not re-fetch them. Null in create mode. */
  existingConfig: TRestaurantPaymentConfig | null;
  availableProviders: TPaymentTerminalProvider[];
  onClose: () => void;
  onSave: (providerId: string, clientFields: Record<string, string>) => void;
  isSaving?: boolean;
};

const RestaurantConfigDialog = ({
  open,
  mode,
  providerId,
  existingConfig,
  availableProviders,
  onClose,
  onSave,
  isSaving,
}: RestaurantConfigDialogProps) => {
  const env = resolveEnv();

  const [selectedProviderId, setSelectedProviderId] = useState('');

  const targetProviderId = mode === 'edit' ? providerId || '' : selectedProviderId;

  const { data: providerCatalog, isLoading: catalogLoading } =
    useGetPaymentProvider(targetProviderId);

  const clientFieldSchema = useMemo(
    () => getClientFieldSchema(providerCatalog, existingConfig),
    [providerCatalog, existingConfig],
  );

  const encryptedKeys = useMemo(
    () => (mode === 'edit' ? existingConfig?.clientFieldsEncryptedKeys || [] : []),
    [mode, existingConfig],
  );

  /**
   * The field list is dynamic per provider, so the validation schema is rebuilt
   * as the catalog loads. It is held in a ref and read by a stable resolver so
   * the schema can depend on data fetched after `useForm` has been called.
   */
  const schemaRef = useRef<z.ZodTypeAny>(z.object({}));
  schemaRef.current = useMemo(
    () => buildClientFieldsSchema(clientFieldSchema, encryptedKeys),
    [clientFieldSchema, encryptedKeys],
  );

  const resolver = useCallback<Resolver<TClientFieldsForm>>(
    (formValues, context, options) =>
      zodResolver(schemaRef.current)(formValues, context, options),
    [],
  );

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TClientFieldsForm>({ mode: 'onChange', resolver, defaultValues: {} });

  // Re-seed whenever the dialog opens or the target provider changes.
  useEffect(() => {
    if (!open) {
      setSelectedProviderId('');
      return;
    }
    if (mode === 'edit') {
      reset({ ...(existingConfig?.clientFields || {}) });
    } else {
      setSelectedProviderId((prev) => prev || availableProviders[0]?.providerId || '');
      reset({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, providerId, existingConfig, availableProviders, reset]);

  const values = watch();

  const fieldValues = useMemo(() => {
    const out: Record<string, string> = {};
    clientFieldSchema.forEach((f) => {
      const v = values[f.id];
      out[f.id] = typeof v === 'string' ? v : '';
    });
    return out;
  }, [values, clientFieldSchema]);

  // Only values ops typed can be checked — stored secrets read back masked.
  const envWarnings = useMemo(() => {
    const typed: Record<string, string> = {};
    Object.entries(fieldValues).forEach(([key, value]) => {
      if (value !== '' && value !== MASK) typed[key] = value;
    });
    return getEnvKeyShapeWarnings(typed, env);
  }, [fieldValues, env]);

  const selectedProvider = useMemo(() => {
    if (mode === 'edit') {
      if (!existingConfig && !providerCatalog) return null;
      return {
        providerId: (existingConfig?.providerId || providerCatalog?.providerId) as string,
        displayName: existingConfig?.displayName || providerCatalog?.displayName,
      };
    }
    const p = availableProviders.find((q) => q.providerId === selectedProviderId);
    if (!p) return null;
    return { providerId: p.providerId, displayName: p.displayName };
  }, [mode, existingConfig, providerCatalog, availableProviders, selectedProviderId]);

  const handleFieldsChange = (next: Record<string, string>) => {
    Object.entries(next).forEach(([key, value]) => {
      if (fieldValues[key] !== value) {
        setValue(key, value, { shouldValidate: true, shouldDirty: true });
      }
    });
  };

  const onSubmit = (form: TClientFieldsForm) => {
    if (!selectedProvider) return;

    const clientFields: Record<string, string> = {};
    clientFieldSchema.forEach((f) => {
      const value = form[f.id];
      if (typeof value !== 'string') return;
      // An untouched or blank secret means "keep the stored value" — sending the
      // mask back would overwrite the real secret with literal bullet characters.
      if (encryptedKeys.includes(f.id) && (value === MASK || value === '')) return;
      clientFields[f.id] = value;
    });

    onSave(selectedProvider.providerId, clientFields);
  };

  const fieldErrors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(errors).map(([key, err]) => [key, (err as { message?: string })?.message]),
      ),
    [errors],
  );

  const noClientFields = !!selectedProvider && clientFieldSchema.length === 0;
  const isFetching = !!targetProviderId && catalogLoading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.title}>
        {mode === 'edit'
          ? `Edit ${selectedProvider?.displayName || selectedProvider?.providerId || 'Provider'} Config`
          : 'Add Provider Config'}
        <Typography sx={styles.subtitle}>
          Restaurant-specific client credentials. Secrets are encrypted on save.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={styles.content}>
          {isFetching ? (
            <Box sx={styles.loaderBox}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {mode === 'create' && (
                <Box mb={2}>
                  {availableProviders.length === 0 ? (
                    <Alert severity="info">
                      All providers are already configured for this restaurant. Edit an existing
                      one or delete it first.
                    </Alert>
                  ) : (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Provider"
                      value={selectedProviderId}
                      onChange={(e) => {
                        setSelectedProviderId(e.target.value);
                        reset({});
                      }}
                      disabled={isSaving}
                    >
                      {availableProviders.map((p) => (
                        <MenuItem key={p.providerId} value={p.providerId}>
                          {p.displayName || p.providerId}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
              )}

              {selectedProvider && !noClientFields && (
                <ProviderFieldsForm
                  schema={clientFieldSchema}
                  values={fieldValues}
                  encryptedKeys={encryptedKeys}
                  onChange={handleFieldsChange}
                  disabled={isSaving}
                  errors={fieldErrors}
                />
              )}

              {envWarnings.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <AlertTitle sx={styles.alertTitle}>Wrong key shape for {env}</AlertTitle>
                  {envWarnings.map((w) => (
                    <Typography key={w.fieldId} sx={styles.alertText}>
                      <strong>{w.fieldId}</strong> starts with <code>{w.prefix}</code>
                    </Typography>
                  ))}
                  <Typography sx={styles.alertText} mt={0.5}>
                    Saving this will make the kiosk silently drop this provider — card payment
                    will be refused with no fallback.
                  </Typography>
                </Alert>
              )}

              {noClientFields && (
                <Alert severity="warning">
                  This provider has no client-scope fields defined yet.
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button sx={styles.cancelBtn} onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          sx={styles.saveBtn}
          onClick={handleSubmit(onSubmit)}
          disabled={
            isSaving ||
            isFetching ||
            !selectedProvider ||
            (mode === 'create' && availableProviders.length === 0)
          }
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RestaurantConfigDialog;

const styles = {
  title: {
    fontFamily: 'GilroyBold',
    fontSize: 18,
  },
  subtitle: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    color: '#666',
    mt: 0.5,
  },
  content: {
    pt: 1,
    minHeight: 160,
  },
  loaderBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 120,
  },
  alertTitle: {
    fontFamily: 'GilroySemiBold',
    fontSize: 14,
  },
  alertText: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
  },
  actions: {
    px: 3,
    py: 2,
  },
  cancelBtn: {
    textTransform: 'capitalize' as const,
    color: '#000',
    fontFamily: 'GilroySemiBold',
    border: '1px solid #000',
    borderRadius: '8px',
    px: 2.5,
  },
  saveBtn: {
    textTransform: 'capitalize' as const,
    backgroundColor: '#000',
    color: '#FFF',
    fontFamily: 'GilroySemiBold',
    borderRadius: '8px',
    px: 2.5,
    '&:hover': { backgroundColor: '#333' },
    '&:disabled': { opacity: 0.5, color: '#FFF' },
  },
};
